import React from 'react';
import { Box, Typography } from '@mui/material';
import { CampaignOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, radii } from '../../styles/tokens';

export type MarketAlertSeverity = 'info' | 'warning' | 'critical';

export interface MarketAlertItemProps {
  title: string;
  market: string;
  timestamp: string;
  severity?: MarketAlertSeverity;
  flag?: string;
  onClick?: () => void;
}

const severityColor: Record<MarketAlertSeverity, string> = {
  info: colors.info,
  warning: colors.warning,
  critical: colors.error,
};

export const MarketAlertItem: React.FC<MarketAlertItemProps> = ({
  title,
  market,
  timestamp,
  severity = 'info',
  flag,
  onClick,
}) => {
  const accent = severityColor[severity];
  const interactive = Boolean(onClick);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        p: 1.5,
        borderRadius: `${radii.button}px`,
        borderLeft: `3px solid ${accent}`,
        bgcolor: alpha(accent, 0.05),
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background-color 150ms ease',
        '&:hover': interactive ? { bgcolor: alpha(accent, 0.1) } : undefined,
      }}
    >
      {flag ? (
        <Box component="span" sx={{ fontSize: 18, lineHeight: 1.2, mt: 0.1, flexShrink: 0 }} aria-hidden>
          {flag}
        </Box>
      ) : (
        <CampaignOutlined sx={{ color: accent, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: colors.textPrimary, lineHeight: 1.4 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
          <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
            {market}
          </Typography>
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textMuted }} />
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {timestamp}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
